/**
 * Phase 20 / Task 20.7 — pure state for §80 step 2 (column mapping).
 *
 * Kept out of the component so the colour rule and the blocking rule are
 * testable without rendering a table.
 *
 * §80's three tiers:
 *   green — a high-confidence auto-match, or a mapping the hauler has confirmed
 *   amber — a low-confidence auto-match the hauler has not yet confirmed
 *   red   — a REQUIRED field nothing maps to (listed above the table; it blocks)
 * A column mapped to Skip is neutral: skipping a column is a choice, not a fault.
 */

import type { ColumnMapping, FamilyMapping, TargetField } from './types';

export type MappingTier = 'HIGH' | 'LOW' | 'SKIP';

export interface FamilyMappingState {
  /** Source column -> template header, or null to skip. */
  readonly mapping: ColumnMapping;
  /** Source columns the hauler has touched or confirmed — no longer amber. */
  readonly confirmed: ReadonlySet<string>;
}

/** Seed from the API's auto-map: a NONE suggestion starts as Skip. */
export function initialFamilyState(family: FamilyMapping): FamilyMappingState {
  const mapping: Record<string, string | null> = {};
  for (const col of family.sourceColumns) mapping[col] = null;
  for (const s of family.suggestions) {
    mapping[s.sourceColumn] = s.confidence === 'NONE' ? null : s.targetHeader;
  }
  return { mapping, confirmed: new Set() };
}

export function tierFor(
  family: FamilyMapping,
  state: FamilyMappingState,
  sourceColumn: string,
): MappingTier {
  const target = state.mapping[sourceColumn] ?? null;
  if (target === null) return 'SKIP';
  if (state.confirmed.has(sourceColumn)) return 'HIGH';
  const suggestion = family.suggestions.find((s) => s.sourceColumn === sourceColumn);
  // A target the auto-map did not propose can only have come from the hauler.
  if (suggestion === undefined || suggestion.targetHeader !== target) return 'HIGH';
  return suggestion.confidence === 'HIGH' ? 'HIGH' : 'LOW';
}

/** Set one column's target; choosing anything counts as confirming it. */
export function setTarget(
  state: FamilyMappingState,
  sourceColumn: string,
  target: string | null,
): FamilyMappingState {
  const confirmed = new Set(state.confirmed);
  confirmed.add(sourceColumn);
  return { mapping: { ...state.mapping, [sourceColumn]: target }, confirmed };
}

/** Confirm every amber suggestion at once, unchanged. */
export function confirmAll(family: FamilyMapping, state: FamilyMappingState): FamilyMappingState {
  return { mapping: state.mapping, confirmed: new Set(family.sourceColumns) };
}

/** Required template headers that no source column maps to — §80's red list. */
export function unmappedRequired(
  targets: readonly TargetField[],
  mapping: ColumnMapping,
): readonly string[] {
  const used = new Set(Object.values(mapping).filter((v): v is string => v !== null));
  return targets.filter((t) => t.required && !used.has(t.header)).map((t) => t.header);
}

/** Template headers chosen for more than one source column. */
export function duplicateTargets(mapping: ColumnMapping): readonly string[] {
  const seen = new Map<string, number>();
  for (const v of Object.values(mapping)) {
    if (v !== null) seen.set(v, (seen.get(v) ?? 0) + 1);
  }
  return [...seen.entries()].filter(([, n]) => n > 1).map(([h]) => h);
}

export function amberCount(family: FamilyMapping, state: FamilyMappingState): number {
  return family.sourceColumns.filter((c) => tierFor(family, state, c) === 'LOW').length;
}

/** Step 2 may continue only when nothing is red and no header is chosen twice. */
export function canContinue(family: FamilyMapping, state: FamilyMappingState): boolean {
  return (
    unmappedRequired(family.targetFields, state.mapping).length === 0 &&
    duplicateTargets(state.mapping).length === 0
  );
}

/**
 * Lay a saved profile over this file. Only the file's own columns are taken:
 * a profile entry for a column the file no longer has is NOT carried, and the
 * validate call reports it by name (20.3 AC-6) rather than it vanishing.
 * Columns the profile says nothing about keep their current mapping.
 */
export function applyProfile(
  family: FamilyMapping,
  state: FamilyMappingState,
  profile: ColumnMapping,
): FamilyMappingState {
  const mapping: Record<string, string | null> = { ...state.mapping };
  const confirmed = new Set(state.confirmed);
  for (const col of family.sourceColumns) {
    if (Object.prototype.hasOwnProperty.call(profile, col)) {
      mapping[col] = profile[col] ?? null;
      confirmed.add(col);
    }
  }
  return { mapping, confirmed };
}

/** Human label for an entity-family key: `DISPOSAL_WELLS` -> `Disposal wells`. */
export function familyLabel(key: string): string {
  const words = key.toLowerCase().split('_').join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}
